<template>
  <div class="mb-6 manager-dashboard-wrapper">
    <div class="d-flex flex-wrap justify-space-between align-end mb-6 px-2" style="gap: 16px">
      <div>
        <div class="text-overline primary--text mb-0" style="letter-spacing: 1.5px; line-height: 1">GL ÜBERSICHT</div>
        <h3 class="font-weight-bold white--text mt-1 text-h5 text-sm-h4">Management Cockpit</h3>
      </div>
      <v-btn icon small @click="fetchData" :loading="loading" class="glass-btn">
        <v-icon color="white" small>mdi-refresh</v-icon>
      </v-btn>
    </div>

    <v-row v-if="loading" class="d-flex justify-center my-12">
      <v-progress-circular indeterminate color="primary" size="60" width="5"></v-progress-circular>
    </v-row>

    <v-row v-else dense class="dashboard-grid">
      <v-col cols="12" sm="12" md="6" lg="4" class="d-flex flex-column mb-sm-4 mb-lg-0">
        <v-card class="kpi-card flex-grow-1 d-flex flex-column" dark elevation="0">
          <div class="kpi-bg-glow blue-glow"></div>

          <div class="pa-5 d-flex flex-column flex-grow-1 justify-space-between relative z-1">
            <div class="d-flex justify-space-between align-start mb-4">
              <div class="pr-2" style="min-width: 0">
                <div class="text-caption grey--text text--lighten-2 text-uppercase font-weight-bold mb-1 text-truncate">Mitglieder Aktuell</div>
                <div class="font-weight-bold white--text text-truncate" :class="$vuetify.breakpoint.lgAndUp ? 'text-h4' : 'text-h5'">
                  {{ formatNumber(stats.currentIst) }}
                </div>
              </div>
              <div class="icon-box blue-box flex-shrink-0">
                <v-icon color="white">mdi-account-group</v-icon>
              </div>
            </div>

            <div>
              <div class="d-flex align-center justify-space-between mb-3 flex-wrap">
                <span class="text-body-2 grey--text mr-2">Plan: {{ formatNumber(stats.currentPlan) }}</span>
                <v-chip
                  x-small
                  label
                  :color="getDiffColor(stats.currentIst, stats.currentPlan)"
                  class="font-weight-bold px-2"
                  style="border-radius: 6px">
                  {{ getDiffPercent(stats.currentIst, stats.currentPlan) }}%
                </v-chip>
              </div>

              <v-divider class="white-10 mb-3"></v-divider>

              <div class="d-flex align-center justify-space-between">
                <span class="text-caption grey--text">Vs. Vormonat</span>
                <div :class="getDiffColorClass(stats.diffAbs)" class="font-weight-bold d-flex align-center text-body-2">
                  <v-icon x-small :color="getDiffColor(stats.diffAbs, 0)" class="mr-1">
                    {{ stats.diffAbs >= 0 ? "mdi-arrow-up" : "mdi-arrow-down" }}
                  </v-icon>
                  {{ stats.diffAbs > 0 ? "+" : "" }}{{ formatNumber(stats.diffAbs) }}
                </div>
              </div>
            </div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="12" md="6" lg="4" class="d-flex flex-column mb-sm-4 mb-lg-0">
        <v-card class="kpi-card flex-grow-1 d-flex flex-column" dark elevation="0">
          <div class="kpi-bg-glow green-glow"></div>

          <div class="pa-5 d-flex flex-column flex-grow-1 justify-space-between relative z-1">
            <div class="d-flex justify-space-between align-start mb-4">
              <div class="pr-2" style="min-width: 0">
                <div class="text-caption grey--text text--lighten-2 text-uppercase font-weight-bold mb-1 text-truncate">Sales (Heute / Gestern)</div>
                <div class="d-flex align-baseline flex-wrap">
                  <span class="font-weight-bold white--text mr-2" :class="$vuetify.breakpoint.lgAndUp ? 'text-h4' : 'text-h5'">
                    {{ stats.today }}
                  </span>
                  <span class="grey--text" :class="$vuetify.breakpoint.lgAndUp ? 'text-h6' : 'text-subtitle-1'"> / {{ stats.yesterday }} </span>
                </div>
              </div>
              <div class="icon-box green-box flex-shrink-0">
                <v-icon color="white">mdi-file-document-edit</v-icon>
              </div>
            </div>

            <div>
              <v-divider class="white-10 mb-3"></v-divider>
              <div class="d-flex justify-space-between align-center py-1">
                <span class="red--text text--lighten-1 font-weight-medium text-body-2 d-flex align-center">
                  <v-icon small color="red lighten-1" class="mr-2">mdi-alert-circle-outline</v-icon>
                  Offene Verträge
                </span>
                <v-chip
                  small
                  color="rgba(244, 67, 54, 0.2)"
                  class="red--text text--lighten-1 font-weight-bold"
                  style="border: 1px solid rgba(244, 67, 54, 0.3)">
                  {{ stats.open }}
                </v-chip>
              </div>
            </div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="12" lg="4" class="d-flex flex-column">
        <v-card color="transparent" elevation="0" class="flex-grow-1 d-flex flex-column">
          <div class="d-flex flex-column flex-grow-1" style="gap: 16px">
            <v-btn
              block
              height="auto"
              min-height="80"
              dark
              class="action-btn px-4 py-3 flex-grow-1"
              @click="$router.push('/dashboard/contract-development')">
              <div class="d-flex align-center w-100">
                <div class="icon-circle mr-4 flex-shrink-0">
                  <v-icon size="24" color="white">mdi-chart-timeline-variant</v-icon>
                </div>
                <div class="d-flex flex-column align-start py-1 text-truncate" style="flex: 1; min-width: 0">
                  <span class="font-weight-bold subtitle-1 white--text text-truncate w-100 text-left">Aboentwicklung</span>
                  <span class="caption grey--text text--lighten-1 text-left">Detailansicht öffnen</span>
                </div>
                <v-icon color="grey lighten-1">mdi-chevron-right</v-icon>
              </div>
            </v-btn>

            <v-btn
              block
              height="auto"
              min-height="80"
              dark
              class="action-btn px-4 py-3 flex-grow-1"
              @click="$router.push('/dashboard/transaction-statistics')">
              <div class="d-flex align-center w-100">
                <div class="icon-circle mr-4 purple-circle flex-shrink-0">
                  <v-icon size="24" color="white">mdi-cash-multiple</v-icon>
                </div>
                <div class="d-flex flex-column align-start py-1 text-truncate" style="flex: 1; min-width: 0">
                  <span class="font-weight-bold subtitle-1 white--text text-truncate w-100 text-left">Transaktionen</span>
                  <span class="caption grey--text text--lighten-1 text-left">Umsätze prüfen</span>
                </div>
                <v-icon color="grey lighten-1">mdi-chevron-right</v-icon>
              </div>
            </v-btn>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapState } from "vuex"
import api from "@/api"

export default {
  name: "ManagerDashboard",
  data() {
    return {
      loading: true,
      stats: {
        currentIst: 0,
        currentPlan: 0,
        prevIst: 0,
        diffAbs: 0,
        today: 0,
        yesterday: 0,
        open: 0,
      },
    }
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),
  },
  async created() {
    await this.fetchData()
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        const [devResponse, miniResponse] = await Promise.all([api.fetchContractDevelopment(), api.fetchHomeMiniStats()])

        if (devResponse && devResponse.centers) {
          this.processContractData(devResponse.centers)
        }

        this.stats.today = miniResponse.today || 0
        this.stats.yesterday = miniResponse.yesterday || 0
        this.stats.open = miniResponse.open || 0
      } catch (error) {
        console.error("Fehler beim Laden des Manager Dashboards:", error)
      } finally {
        this.loading = false
      }
    },

    processContractData(centersData) {
      if (!centersData) return

      const now = new Date()
      const currentKey = this.getDateKey(now)

      const prevDate = new Date()
      prevDate.setMonth(prevDate.getMonth() - 1)
      const prevKey = this.getDateKey(prevDate)

      let totalIst = 0
      let totalPrev = 0
      let totalPlan = 0

      Object.keys(centersData).forEach((centerId) => {
        const centerMonths = centersData[centerId]

        const istVal = this.extractValue(centerMonths[currentKey])
        const prevVal = this.extractValue(centerMonths[prevKey])

        totalIst += istVal
        totalPrev += prevVal

        if (this.storeStudios && this.storeStudios.length > 0) {
          const storeCenter = this.storeStudios.find((s) => s.code === centerId)
          if (storeCenter) {
            const growthData = storeCenter.growth ? storeCenter.growth.find((g) => g.date === currentKey) : null
            let planVal = prevVal
            if (growthData) {
              planVal += Number(growthData.count)
            }
            totalPlan += planVal
          }
        }
      })

      this.stats.currentIst = totalIst
      this.stats.prevIst = totalPrev
      this.stats.currentPlan = totalPlan
      this.stats.diffAbs = totalIst - totalPrev
    },

    extractValue(data) {
      if (!data) return 0
      if (typeof data === "number") return data
      if (typeof data === "object" && data.active !== undefined) return data.active
      return 0
    },

    getDateKey(date) {
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`
    },

    formatNumber(val) {
      return val ? val.toLocaleString("de-CH") : "0"
    },

    getDiffColor(ist, plan) {
      if (!plan) return "grey"
      return ist >= plan ? "rgba(0, 230, 118, 0.2)" : "rgba(255, 82, 82, 0.2)"
    },

    getDiffColorClass(val) {
      if (val === 0) return "grey--text"
      return val > 0 ? "success--text" : "error--text"
    },

    getDiffPercent(ist, plan) {
      if (!plan || plan === 0) return 0
      const diff = ist - plan
      return ((diff / plan) * 100).toFixed(1)
    },
  },
}
</script>

<style scoped lang="scss">
.manager-dashboard-wrapper {
  font-family: "Roboto", sans-serif;
}

/* Glassmorphism Button */
.glass-btn {
  background: rgba(255, 255, 255, 0.1) !important;
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

/* KPI Cards */
.kpi-card {
  background: linear-gradient(145deg, rgba(30, 35, 45, 0.9) 0%, rgba(20, 25, 35, 0.95) 100%) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  border-radius: 20px !important; /* Etwas runder */
  position: relative;
  overflow: hidden;
  transition: transform 0.2s;
  height: 100%; /* Wichtig für gleiche Höhe */
}

.kpi-bg-glow {
  position: absolute;
  top: -50px;
  right: -50px;
  width: 150px;
  height: 150px;
  border-radius: 50%;
  filter: blur(60px);
  opacity: 0.15;
}

.blue-glow {
  background: #2196f3;
}
.green-glow {
  background: #4caf50;
}

.relative {
  position: relative;
}
.z-1 {
  z-index: 1;
}

.icon-box {
  width: 48px;
  height: 48px; /* Etwas größer */
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}
.blue-box {
  background: linear-gradient(135deg, #2196f3, #1976d2);
}
.green-box {
  background: linear-gradient(135deg, #4caf50, #388e3c);
}

.white-10 {
  border-color: rgba(255, 255, 255, 0.1) !important;
}

/* Action Buttons - Höhenoptimiert */
.action-btn {
  background: rgba(40, 44, 52, 0.6) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.05) !important;
  border-radius: 20px !important;
  text-transform: none;
  letter-spacing: normal;
  transition: all 0.2s;

  ::v-deep .v-btn__content {
    width: 100%;
    justify-content: flex-start;
  }
}
.action-btn:hover {
  background: rgba(50, 54, 62, 0.8) !important;
  border-color: rgba(255, 255, 255, 0.15) !important;
  transform: translateY(-2px);
}

.icon-circle {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
}
.purple-circle {
  background: rgba(156, 39, 176, 0.2);
  border: 1px solid rgba(156, 39, 176, 0.3);
}

/* Wichtig: Globaler Tooltip Style Fix für ApexCharts */
::v-deep .apexcharts-tooltip {
  background: rgba(30, 30, 40, 0.95) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.5) !important;
  border-radius: 8px !important;
  color: #fff;
  z-index: 9999 !important; /* Damit er sicher oben liegt */
}
::v-deep .apexcharts-tooltip-title {
  background: rgba(0, 0, 0, 0.2) !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
  font-family: inherit !important;
}
::v-deep .apexcharts-tooltip-text {
  color: #fff !important;
  font-family: inherit !important;
}
</style>
